import { useId, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info, Lock, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { useLabel } from '@/lib/labels';
import { Button } from '@/components/Button';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * `/me/family` (v0.4 休暇管理 TD-26 §6-1 L-5)。本人が自分の家族情報を登録・訂正する画面。
 *
 * ⚠️⚠️ 本プロダクトで最も機微な画面 (§5-5 冒頭)。要配慮個人情報 (要介護状態) を扱う。
 *
 * ⚠️ §6-3 ① / D-80: 要配慮項目 (`requires_care_level`) の入力欄は、同意が有効なときだけ
 *    **DOM に出さない** (disabled で出さない)。`disabled` にすると「そういう項目を会社が
 *    持っている」こと自体が同意前の画面に現れてしまうため。実装箇所は MemberFormModal /
 *    MemberCard の `hasCareConsent` / `member.has_sensitive_care_consent` による条件付きレンダリング。
 * ⚠️ §1-8-1 C-2: 同意の有無の判定はサーバーが毎回 `employee_family_consent_events` から
 *    解決した `has_sensitive_care_consent` のみを使う。`consent_obtained_at` /
 *    `consent_withdrawn_at` は**表示用のキャッシュ**であり、この画面でも判定に使わない
 *    (記録の履歴としてのみ表示する)。
 * ⚠️ 撤回 (`withdrawn`) すると読取り時ゲートにより `requires_care_level` はサーバーから
 *    返らなくなる。撤回できること・撤回すると見えなくなることを画面に明記する。
 *    撤回は本人の正当な権利のため、破壊的操作としては見せない (赤・警告アイコンを使わない)。
 * ⚠️ §13-2 ③: 診断書・出生証明等の書類のアップロード欄は作らない。
 * ⚠️ §13-2 ⑦: 家族情報に関する AI 機能は作らない。
 *
 * 設計: docs/design/v04-leave-management-detail.md §1-7 / §1-8-1 / §5-5 / §6-3
 * API 契約: apps/api/src/routes/employee-family.ts (meFamilyRoutes) /
 *           apps/api/src/lib/leave/family-visibility.ts (projectFamilyMember)
 * Full 限定・`family_member_management` フラグ配下。
 */

type FamilyMember = {
  family_member_id: string;
  relationship_code: string;
  family_name: string | null;
  given_name: string | null;
  family_name_kana: string | null;
  given_name_kana: string | null;
  birth_date: string | null;
  is_cohabiting: boolean | null;
  is_dependent: boolean | null;
  expected_birth_date: string | null;
  actual_birth_date: string | null;
  is_multiple_pregnancy: boolean | null;
  /** ⚠️ 表示用キャッシュ。判定には使わない (§1-8-1 C-2)。 */
  consent_obtained_at: string | null;
  consent_method: string | null;
  consent_withdrawn_at: string | null;
  status: string;
  row_version: number;
  /** ⚠️ 要配慮項目を出してよいかの唯一の判定材料。 */
  has_sensitive_care_consent: boolean;
  /** ⚠️ 同意があるときだけサーバーが返す (無いときはキーごと落ちる)。 */
  requires_care_level?: string | null;
  care_status_recorded_at?: string | null;
  care_status_recorded_by_user_id?: string | null;
};

type Relationship = {
  relationship_code: string;
  label: string;
  kinship_degree: number | null;
  is_care_target: boolean;
  is_child: boolean;
};

// apps/api/src/lib/leave-constants.ts の zod enum と対応 (CONSENT_EVENT_KINDS / _SCOPES / _METHODS)
const CONSENT_EVENT_KINDS = [
  { value: 'obtained', label: '同意を取得した' },
  { value: 're_obtained', label: '撤回後に改めて同意を取得した' },
  { value: 'withdrawn', label: '同意を撤回する' },
] as const;

const CONSENT_SCOPES = [
  { value: 'sensitive_care_status', label: '要介護状態などの要配慮情報' },
  { value: 'family_basic_info', label: '家族の基本情報' },
] as const;

const CONSENT_METHODS = [
  { value: 'self_declared', label: '本人の申告' },
  { value: 'written', label: '書面' },
  { value: 'electronic', label: '電磁的記録' },
] as const;

type ConsentEventKind = (typeof CONSENT_EVENT_KINDS)[number]['value'];
type ConsentScope = (typeof CONSENT_SCOPES)[number]['value'];
type ConsentMethod = (typeof CONSENT_METHODS)[number]['value'];

const consentMethodLabel = (v: string | null) =>
  CONSENT_METHODS.find((m) => m.value === v)?.label ?? v ?? '—';

const emptyToNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

const fullName = (m: FamilyMember) => {
  const name = [m.family_name, m.given_name].filter(Boolean).join(' ');
  return name === '' ? '(氏名の登録なし)' : name;
};

const kanaName = (m: FamilyMember) => [m.family_name_kana, m.given_name_kana].filter(Boolean).join(' ');

const describeError = (e: unknown, fallback: string) =>
  e instanceof ApiClientError ? e.message : fallback;

const isConflict = (e: unknown) => e instanceof ApiClientError && e.status === 409;

type MemberDraft = {
  relationship_code: string;
  family_name: string;
  given_name: string;
  family_name_kana: string;
  given_name_kana: string;
  birth_date: string;
  is_cohabiting: boolean;
  is_dependent: boolean;
  expected_birth_date: string;
  actual_birth_date: string;
  is_multiple_pregnancy: boolean;
  /** ⚠️ 同意が有効なときのみ入力欄を出す。同意が無ければ送信もしない。 */
  requires_care_level: string;
};

const emptyDraft: MemberDraft = {
  relationship_code: '', family_name: '', given_name: '',
  family_name_kana: '', given_name_kana: '', birth_date: '',
  is_cohabiting: false, is_dependent: false,
  expected_birth_date: '', actual_birth_date: '', is_multiple_pregnancy: false,
  requires_care_level: '',
};

const toDraft = (m: FamilyMember): MemberDraft => ({
  relationship_code: m.relationship_code,
  family_name: m.family_name ?? '', given_name: m.given_name ?? '',
  family_name_kana: m.family_name_kana ?? '', given_name_kana: m.given_name_kana ?? '',
  birth_date: m.birth_date ?? '',
  is_cohabiting: m.is_cohabiting === true, is_dependent: m.is_dependent === true,
  expected_birth_date: m.expected_birth_date ?? '', actual_birth_date: m.actual_birth_date ?? '',
  is_multiple_pregnancy: m.is_multiple_pregnancy === true,
  requires_care_level: m.requires_care_level ?? '',
});

const inputCls =
  'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border bg-white text-sm text-text-body '
  + 'focus:border-chrome-base focus:outline-none';

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return <label htmlFor={htmlFor} className="block text-xs font-bold text-text-sub mb-1">{children}</label>;
}

function CheckboxRow({
  id, checked, onChange, children,
}: { id: string; checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 min-h-[44px] text-sm text-text-body cursor-pointer">
      <input id={id} type="checkbox" className="w-5 h-5 shrink-0 accent-chrome-base"
        checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{children}</span>
    </label>
  );
}

/** テナントの責任分界を明記する定型文 (§6-3 ① / D-80)。同意を扱う全ての箇所に出す。 */
function ConsentResponsibilityNote() {
  return (
    <p className="text-xs text-text-sub leading-relaxed">
      要介護状態などの要配慮個人情報は、本人の同意がある場合にのみ記録・表示されます。
      <strong className="font-bold text-text-body">同意の取得と適法性の担保は、テナント (会社) の責任です。</strong>
      MiseSpark は同意の記録を保管するのみで、同意が適法に取得されたことを保証しません。
    </p>
  );
}

// =============================================================================
// 家族情報の登録・編集モーダル
// =============================================================================
function MemberFormModal({
  open, mode, member, relationships, onClose,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  member: FamilyMember | null;
  relationships: Relationship[];
  onClose: () => void;
}) {
  const t = useLabel();
  const qc = useQueryClient();
  const uid = useId();
  const [draft, setDraft] = useState<MemberDraft>(() => (member ? toDraft(member) : emptyDraft));
  // モーダルは open のたびに key で作り直す (呼び出し側) ため、初期化は useState で足りる。

  // ⚠️ §1-8-1 C-2: 同意の有無はサーバーが毎回イベントから解決した値のみを使う。
  //    consent_obtained_at (表示用キャッシュ) では判定しない。
  const hasCareConsent = mode === 'edit' && member?.has_sensitive_care_consent === true;
  const selectedRelationship = relationships.find((r) => r.relationship_code === draft.relationship_code);

  const set = <K extends keyof MemberDraft>(k: K, v: MemberDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const buildCommonBody = () => ({
    relationship_code: draft.relationship_code,
    family_name: emptyToNull(draft.family_name),
    given_name: emptyToNull(draft.given_name),
    family_name_kana: emptyToNull(draft.family_name_kana),
    given_name_kana: emptyToNull(draft.given_name_kana),
    birth_date: emptyToNull(draft.birth_date),
    is_cohabiting: draft.is_cohabiting,
    is_dependent: draft.is_dependent,
    expected_birth_date: emptyToNull(draft.expected_birth_date),
    actual_birth_date: emptyToNull(draft.actual_birth_date),
    is_multiple_pregnancy: draft.is_multiple_pregnancy,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (mode === 'create') {
        // ⚠️ POST は requires_care_level を受け付けない (API 側が .strict() で拒否する)。
        //    登録 → 同意を記録 → はじめて要介護状態を入力できる、という順序になる。
        return apiFetch<FamilyMember>('/me/family-members', {
          method: 'POST', body: JSON.stringify(buildCommonBody()),
        });
      }
      const body: Record<string, unknown> = { ...buildCommonBody(), row_version: member!.row_version };
      // ⚠️ 同意が無いときは requires_care_level をキーごと送らない (サーバー側の書込みゲートに
      //    弾かれるだけでなく、同意前に「値を持ちうる」経路を画面から作らないため)。
      if (hasCareConsent) body.requires_care_level = emptyToNull(draft.requires_care_level);
      return apiFetch<FamilyMember>(`/me/family-members/${member!.family_member_id}`, {
        method: 'PUT', body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me-family-members'] });
      toast.success(mode === 'create' ? '家族情報を登録しました' : '家族情報を更新しました');
      onClose();
    },
    onError: (e) => {
      // ⚠️ 楽観ロック: 409 は握り潰さず、再読み込みを促したうえで最新を取り直す。
      qc.invalidateQueries({ queryKey: ['me-family-members'] });
      toast.error(isConflict(e)
        ? `${describeError(e, '他の変更と競合しました')} (最新の内容を読み込み直しました)`
        : describeError(e, '保存に失敗しました'));
      if (isConflict(e)) onClose();
    },
  });

  return (
    <Modal
      open={open}
      title={mode === 'create' ? '家族を登録' : '家族情報を編集'}
      size="md"
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saveMut.isPending}>キャンセル</Button>
          <Button variant="primary" onClick={() => saveMut.mutate()}
            disabled={draft.relationship_code === '' || saveMut.isPending} loading={saveMut.isPending}>
            保存する
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <FieldLabel htmlFor={`${uid}-rel`}>{t('field.employee_family_members.relationship_code', '続柄')} (必須)</FieldLabel>
          <Select value={draft.relationship_code || undefined} onValueChange={(v) => set('relationship_code', v)}>
            <SelectTrigger id={`${uid}-rel`} className="min-h-[44px]">
              <SelectValue placeholder="続柄を選ぶ" />
            </SelectTrigger>
            <SelectContent>
              {relationships.map((r) => (
                <SelectItem key={r.relationship_code} value={r.relationship_code}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {relationships.length === 0 && (
            <p className="text-xs text-text-sub mt-1">続柄の選択肢が取得できていません。再読み込みしてください。</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <FieldLabel htmlFor={`${uid}-fn`}>{t('field.employee_family_members.family_name', '姓')}</FieldLabel>
            <input id={`${uid}-fn`} className={inputCls} value={draft.family_name}
              onChange={(e) => set('family_name', e.target.value)} />
          </div>
          <div>
            <FieldLabel htmlFor={`${uid}-gn`}>{t('field.employee_family_members.given_name', '名')}</FieldLabel>
            <input id={`${uid}-gn`} className={inputCls} value={draft.given_name}
              onChange={(e) => set('given_name', e.target.value)} />
          </div>
          <div>
            <FieldLabel htmlFor={`${uid}-fk`}>{t('field.employee_family_members.family_name_kana', '姓（カナ）')}</FieldLabel>
            <input id={`${uid}-fk`} className={inputCls} value={draft.family_name_kana}
              onChange={(e) => set('family_name_kana', e.target.value)} />
          </div>
          <div>
            <FieldLabel htmlFor={`${uid}-gk`}>{t('field.employee_family_members.given_name_kana', '名（カナ）')}</FieldLabel>
            <input id={`${uid}-gk`} className={inputCls} value={draft.given_name_kana}
              onChange={(e) => set('given_name_kana', e.target.value)} />
          </div>
          <div>
            <FieldLabel htmlFor={`${uid}-bd`}>{t('field.employee_family_members.birth_date', '生年月日')}</FieldLabel>
            <input id={`${uid}-bd`} type="date" className={inputCls} value={draft.birth_date}
              onChange={(e) => set('birth_date', e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <CheckboxRow id={`${uid}-coh`} checked={draft.is_cohabiting} onChange={(v) => set('is_cohabiting', v)}>
            {t('field.employee_family_members.is_cohabiting', '同居している')}
          </CheckboxRow>
          <CheckboxRow id={`${uid}-dep`} checked={draft.is_dependent} onChange={(v) => set('is_dependent', v)}>
            {t('field.employee_family_members.is_dependent', '扶養している')}
          </CheckboxRow>
        </div>

        {/* 出産に関する情報は続柄が「子」の場合のみ。無関係な続柄で機微な欄を出さない。 */}
        {selectedRelationship?.is_child && (
          <fieldset className="border border-surface-line rounded p-3 space-y-3">
            <legend className="px-1 text-xs font-bold text-text-sub">出産に関する情報 (任意)</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <FieldLabel htmlFor={`${uid}-ebd`}>{t('field.employee_family_members.expected_birth_date', '出産予定日')}</FieldLabel>
                <input id={`${uid}-ebd`} type="date" className={inputCls} value={draft.expected_birth_date}
                  onChange={(e) => set('expected_birth_date', e.target.value)} />
              </div>
              <div>
                <FieldLabel htmlFor={`${uid}-abd`}>{t('field.employee_family_members.actual_birth_date', '出生日')}</FieldLabel>
                <input id={`${uid}-abd`} type="date" className={inputCls} value={draft.actual_birth_date}
                  onChange={(e) => set('actual_birth_date', e.target.value)} />
              </div>
            </div>
            <CheckboxRow id={`${uid}-mp`} checked={draft.is_multiple_pregnancy}
              onChange={(v) => set('is_multiple_pregnancy', v)}>
              {t('field.employee_family_members.is_multiple_pregnancy', '多胎妊娠')}
            </CheckboxRow>
          </fieldset>
        )}

        {/* ⚠️⚠️ §6-3 ① / D-80 の中核: 要配慮項目は同意が有効なときだけ DOM に出す。
            hasCareConsent が false のとき、この分岐は入力欄そのものをレンダリングしない
            (disabled 属性で無効化するのではない)。 */}
        {hasCareConsent ? (
          <fieldset className="border border-surface-border rounded p-3 space-y-2 bg-surface-sunken">
            <legend className="px-1 text-xs font-bold text-text-sub">要配慮個人情報</legend>
            <div>
              <FieldLabel htmlFor={`${uid}-care`}>{t('field.employee_family_members.requires_care_level', '要介護状態')} (要介護度など)</FieldLabel>
              <input id={`${uid}-care`} className={inputCls} value={draft.requires_care_level}
                placeholder="例: 要介護2"
                onChange={(e) => set('requires_care_level', e.target.value)} />
            </div>
            <p className="text-xs text-text-sub">
              空欄にして保存すると、記録済みの要介護状態を削除できます。
            </p>
            <ConsentResponsibilityNote />
          </fieldset>
        ) : mode === 'edit' ? (
          <div className="rounded border border-surface-line bg-surface-sunken px-3 py-2 flex items-start gap-2">
            <Lock size={16} className="text-text-sub shrink-0 mt-0.5" aria-hidden />
            <p className="text-xs text-text-sub leading-relaxed">
              要介護状態などの要配慮個人情報は、同意を記録するまで入力できません。
              このモーダルを閉じ、一覧の「同意を記録」から手続きしてください。
            </p>
          </div>
        ) : (
          <div className="rounded border border-surface-line bg-surface-sunken px-3 py-2 flex items-start gap-2">
            <Info size={16} className="text-text-sub shrink-0 mt-0.5" aria-hidden />
            <p className="text-xs text-text-sub leading-relaxed">
              要介護状態などの要配慮個人情報は、この登録では入力できません。登録後に同意を記録すると入力欄が現れます。
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

// =============================================================================
// 同意の記録モーダル (取得 / 再取得 / 撤回)
// =============================================================================
function ConsentModal({
  open, member, initialEventKind, onClose,
}: {
  open: boolean;
  member: FamilyMember;
  initialEventKind: ConsentEventKind;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const uid = useId();
  const [eventKind, setEventKind] = useState<ConsentEventKind>(initialEventKind);
  const [consentScope, setConsentScope] = useState<ConsentScope>('sensitive_care_status');
  const [consentMethod, setConsentMethod] = useState<ConsentMethod>('self_declared');
  const [note, setNote] = useState('');

  const isWithdrawal = eventKind === 'withdrawn';

  const mut = useMutation({
    mutationFn: () => apiFetch<FamilyMember>(`/me/family-members/${member.family_member_id}/consent`, {
      method: 'POST',
      body: JSON.stringify({
        event_kind: eventKind,
        consent_scope: consentScope,
        consent_method: consentMethod,
        note: emptyToNull(note),
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me-family-members'] });
      toast.success(isWithdrawal ? '同意の撤回を記録しました' : '同意を記録しました');
      onClose();
    },
    onError: (e) => {
      qc.invalidateQueries({ queryKey: ['me-family-members'] });
      toast.error(isConflict(e)
        ? `${describeError(e, '他の変更と競合しました')} (最新の内容を読み込み直しました)`
        : describeError(e, '同意の記録に失敗しました'));
    },
  });

  return (
    <Modal
      open={open}
      title={`同意の記録 — ${fullName(member)}`}
      size="md"
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>キャンセル</Button>
          {/* 撤回は本人の正当な権利のため danger では見せない。 */}
          <Button variant={isWithdrawal ? 'secondary' : 'primary'} onClick={() => mut.mutate()}
            disabled={mut.isPending} loading={mut.isPending}>
            {isWithdrawal ? '撤回を記録する' : '同意を記録する'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <ConsentResponsibilityNote />

        <div>
          <FieldLabel htmlFor={`${uid}-kind`}>記録する内容</FieldLabel>
          <Select value={eventKind} onValueChange={(v) => setEventKind(v as ConsentEventKind)}>
            <SelectTrigger id={`${uid}-kind`} className="min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONSENT_EVENT_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <FieldLabel htmlFor={`${uid}-scope`}>対象となる情報</FieldLabel>
          <Select value={consentScope} onValueChange={(v) => setConsentScope(v as ConsentScope)}>
            <SelectTrigger id={`${uid}-scope`} className="min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONSENT_SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-text-sub mt-1">
            要介護状態の入力欄が現れるのは「要介護状態などの要配慮情報」を選んで同意した場合のみです。
          </p>
        </div>

        <div>
          <FieldLabel htmlFor={`${uid}-method`}>同意の取得方法</FieldLabel>
          <Select value={consentMethod} onValueChange={(v) => setConsentMethod(v as ConsentMethod)}>
            <SelectTrigger id={`${uid}-method`} className="min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONSENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <FieldLabel htmlFor={`${uid}-note`}>備考 (任意・500文字まで)</FieldLabel>
          <textarea id={`${uid}-note`} rows={2} maxLength={500} value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2 rounded border border-surface-border bg-white text-sm text-text-body focus:border-chrome-base focus:outline-none" />
        </div>

        {isWithdrawal && (
          <div className="rounded border border-surface-border bg-surface-sunken px-3 py-2 flex items-start gap-2">
            <Info size={16} className="text-text-sub shrink-0 mt-0.5" aria-hidden />
            <p className="text-xs text-text-body leading-relaxed">
              撤回すると、記録済みの要介護状態は<strong className="font-bold">画面にも API にも表示されなくなります</strong>
              (入力欄も消えます)。撤回した記録自体は同意の履歴として保管されます。あとから改めて同意を記録することもできます。
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

// =============================================================================
// 一覧のカード
// =============================================================================
function MemberCard({
  member, relationshipLabel, canUpdate, canDelete, onEdit, onConsent, onDelete,
}: {
  member: FamilyMember;
  relationshipLabel: string;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onConsent: (kind: ConsentEventKind) => void;
  onDelete: () => void;
}) {
  const t = useLabel();
  const kana = kanaName(member);
  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs font-bold text-text-sub">{relationshipLabel}</p>
          <p className="text-card-title text-text-main break-words">{fullName(member)}</p>
          {kana !== '' && <p className="text-xs text-text-sub break-words">{kana}</p>}
        </div>
        <div className="flex items-center gap-2">
          {canUpdate && (
            <Button variant="ghost" size="sm" leftIcon={<Pencil size={14} aria-hidden />} onClick={onEdit}>編集</Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="sm" leftIcon={<Trash2 size={14} aria-hidden />} onClick={onDelete}>削除</Button>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <div className="flex gap-2">
          <dt className="text-text-sub shrink-0">{t('field.employee_family_members.birth_date', '生年月日')}</dt>
          <dd className="text-text-body break-words">{member.birth_date ?? '—'}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-text-sub shrink-0">同居 / 扶養</dt>
          <dd className="text-text-body">
            {member.is_cohabiting === true ? '同居' : '別居'} / {member.is_dependent === true ? '扶養あり' : '扶養なし'}
          </dd>
        </div>
        {member.expected_birth_date && (
          <div className="flex gap-2">
            <dt className="text-text-sub shrink-0">出産予定日</dt>
            <dd className="text-text-body">{member.expected_birth_date}{member.is_multiple_pregnancy === true ? ' (多胎)' : ''}</dd>
          </div>
        )}
        {member.actual_birth_date && (
          <div className="flex gap-2">
            <dt className="text-text-sub shrink-0">出生日</dt>
            <dd className="text-text-body">{member.actual_birth_date}</dd>
          </div>
        )}
      </dl>

      {/* ⚠️ §6-3 ① / §1-8-1 C-2: 要配慮情報の表示も同意が有効なときだけ。
          判定は has_sensitive_care_consent のみ (consent_obtained_at は使わない)。 */}
      <div className="border-t border-surface-line pt-3 space-y-2">
        {member.has_sensitive_care_consent ? (
          <>
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-success-text shrink-0" aria-hidden />
              <span className="text-xs font-bold text-success-text">要配慮情報の同意あり</span>
            </div>
            <p className="text-sm text-text-body break-words">
              <span className="text-text-sub">{t('field.employee_family_members.requires_care_level', '要介護状態')}: </span>
              {member.requires_care_level ?? '未入力'}
            </p>
            {member.care_status_recorded_at && (
              <p className="text-xs text-text-sub">最終記録: {member.care_status_recorded_at}</p>
            )}
            <p className="text-xs text-text-sub">
              取得方法: {consentMethodLabel(member.consent_method)}
              {member.consent_obtained_at ? ` / 記録日時: ${member.consent_obtained_at}` : ''}
            </p>
            {canUpdate && (
              <Button variant="secondary" size="sm" onClick={() => onConsent('withdrawn')}>同意を撤回する</Button>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Lock size={16} className="text-text-sub shrink-0" aria-hidden />
              <span className="text-xs font-bold text-text-sub">要配慮情報の同意なし</span>
            </div>
            <p className="text-xs text-text-sub leading-relaxed">
              要介護状態などの要配慮個人情報は記録されていません。同意を記録すると入力欄が現れます。
              {member.consent_withdrawn_at && ` (${member.consent_withdrawn_at} に撤回の記録があります)`}
            </p>
            {canUpdate && (
              <Button variant="secondary" size="sm"
                onClick={() => onConsent(member.consent_withdrawn_at ? 're_obtained' : 'obtained')}>
                同意を記録
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
export function MyFamilyPage() {
  const t = useLabel();
  const user = useAuthStore((s) => s.user);
  const perms = user?.effective_permissions;
  const nonInheriting = user?.non_inheriting_features;
  const permsReady = usePermsReady();
  const canView = hasPermission(perms, 'employee_family', 'view', nonInheriting);
  const canCreate = hasPermission(perms, 'employee_family', 'create', nonInheriting);
  const canUpdate = hasPermission(perms, 'employee_family', 'update', nonInheriting);
  const canDelete = hasPermission(perms, 'employee_family', 'delete', nonInheriting);

  const qc = useQueryClient();
  const [formState, setFormState] = useState<{ mode: 'create' | 'edit'; member: FamilyMember | null } | null>(null);
  const [consentTarget, setConsentTarget] = useState<{ member: FamilyMember; kind: ConsentEventKind } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FamilyMember | null>(null);

  // ⚠️ not_linked_to_employee: このアカウントが社員情報 (user_person_links) に紐付いていない状態。
  //    §8-125 の対応後も「紐付いていない人は本人を特定できない」ため登録できない。空一覧と
  //    区別して案内しないと「登録ボタンを押しても 403」の dead-end に見える (QA レビュー HIGH)。
  const listQuery = useQuery<{ family_members: FamilyMember[]; not_linked_to_employee?: boolean }>({
    queryKey: ['me-family-members'],
    queryFn: () => apiFetch<{ family_members: FamilyMember[]; not_linked_to_employee?: boolean }>('/me/family-members'),
  });
  const relQuery = useQuery<{ relationships: Relationship[] }>({
    queryKey: ['family-relationships'],
    queryFn: () => apiFetch<{ relationships: Relationship[] }>('/family-relationships'),
    staleTime: 10 * 60_000,
  });

  const relationships = relQuery.data?.relationships ?? [];
  const relationshipLabels = useMemo(
    () => new Map(relationships.map((r) => [r.relationship_code, r.label])),
    [relationships],
  );

  const deleteMut = useMutation({
    mutationFn: (m: FamilyMember) =>
      apiFetch(`/me/family-members/${m.family_member_id}?row_version=${m.row_version}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me-family-members'] });
      toast.success('家族情報を削除しました');
      setDeleteTarget(null);
    },
    onError: (e) => {
      qc.invalidateQueries({ queryKey: ['me-family-members'] });
      toast.error(isConflict(e)
        ? `${describeError(e, '他の変更と競合しました')} (最新の内容を読み込み直しました)`
        : describeError(e, '削除に失敗しました'));
      setDeleteTarget(null);
    },
  });

  // フラグ OFF / 権限なしは「エラー」ではなく状態として落ち着いて伝える。
  const listError = listQuery.error;
  const featureDisabled = listError instanceof ApiClientError && listError.code === 'FEATURE_DISABLED';
  const forbidden = listError instanceof ApiClientError && listError.status === 403 && !featureDisabled;

  const intro = (
    <div className="rounded border border-surface-line bg-surface-sunken px-3 py-2 flex items-start gap-2">
      <Info size={16} className="text-text-sub shrink-0 mt-0.5" aria-hidden />
      <p className="text-sm text-text-body leading-relaxed">
        介護休業・子の看護休暇など、家族に関わる{t('entity.leave_requests', '休暇申請')}の資格判定に使われる情報です。
        ここに登録できるのは自分の家族の情報だけで、他の人は見られません。
      </p>
    </div>
  );

  const body = () => {
    if (featureDisabled) {
      return (
        <div className="border border-surface-border rounded-lg bg-white p-6 text-center">
          <p className="text-sm text-text-body">この機能は利用できません。</p>
          <p className="text-xs text-text-sub mt-1">家族情報の管理は会社の設定で無効になっています。</p>
        </div>
      );
    }
    if (!permsReady) return <div role="status" className="text-text-sub">読み込み中…</div>;
    if (forbidden || !canView) {
      return (
        <div className="border border-surface-border rounded-lg bg-white p-6 text-center">
          <p className="text-sm text-text-body">家族情報を閲覧する権限がありません。</p>
        </div>
      );
    }
    if (listQuery.isLoading) return <div role="status" className="text-text-sub">読み込み中…</div>;
    if (listQuery.isError) {
      return (
        <p role="alert" className="text-sm text-alert-text">
          {describeError(listError, '家族情報の取得に失敗しました')}
        </p>
      );
    }
    // ⚠️ §8-60: data === undefined を「データが無い」と断定しない (paused 等でも undefined になる)。
    if (!listQuery.data) {
      return <p role="status" className="text-sm text-text-sub">家族情報を取得できていません。再読み込みしてください。</p>;
    }
    if (listQuery.data.not_linked_to_employee) {
      return (
        <div className="border border-surface-border rounded-lg bg-white p-6 space-y-2">
          <p className="text-sm text-text-body">この画面はご本人の家族情報を登録するためのものです。</p>
          <p className="text-xs text-text-sub">
            お使いのアカウントが社員情報と紐付いていないため、登録できる本人を特定できません。
            紐付けは管理者が「社員」画面から行えます。
          </p>
        </div>
      );
    }
    const members = listQuery.data.family_members;
    if (members.length === 0) {
      return (
        <div className="border border-surface-border rounded-lg bg-white p-6 text-center space-y-2">
          <p className="text-sm text-text-body">登録されている家族情報はありません。</p>
          <p className="text-xs text-text-sub">介護休業などを申請する場合は、対象となるご家族を登録してください。</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {members.map((m) => (
          <MemberCard
            key={m.family_member_id}
            member={m}
            relationshipLabel={relationshipLabels.get(m.relationship_code) ?? m.relationship_code}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onEdit={() => setFormState({ mode: 'edit', member: m })}
            onConsent={(kind) => setConsentTarget({ member: m, kind })}
            onDelete={() => setDeleteTarget(m)}
          />
        ))}
      </div>
    );
  };

  // ⚠️ 紐付いていないアカウントでは登録が必ず 403 になるため「家族を登録」を出さない
  //    (押しても必ず失敗するボタンを出さない、F-4/D-209 と同型の判断)。
  const notLinked = listQuery.data?.not_linked_to_employee === true;
  const showActions = !featureDisabled && !forbidden && canView && !notLinked;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h1 className="text-page-title text-text-main">{t('entity.employee_family_members', '家族情報')}</h1>
        {showActions && canCreate && (
          <Button variant="primary" leftIcon={<Plus size={16} aria-hidden />}
            onClick={() => setFormState({ mode: 'create', member: null })}>
            家族を登録
          </Button>
        )}
      </div>

      {intro}

      <div className="rounded border border-alert-border bg-alert-bg px-3 py-2 flex items-start gap-2">
        <AlertTriangle size={16} className="text-alert-text shrink-0 mt-0.5" aria-hidden />
        <p className="text-xs text-alert-text leading-relaxed">
          要介護状態などの要配慮個人情報は、同意を記録するまで入力欄が表示されません。
          同意の取得と適法性の担保はテナント (会社) の責任です。
          診断書などの書類はここでは扱いません。
        </p>
      </div>

      {body()}

      {formState && (
        <MemberFormModal
          key={formState.member?.family_member_id ?? 'new'}
          open
          mode={formState.mode}
          member={formState.member}
          relationships={relationships}
          onClose={() => setFormState(null)}
        />
      )}

      {consentTarget && (
        <ConsentModal
          key={`${consentTarget.member.family_member_id}-${consentTarget.kind}`}
          open
          member={consentTarget.member}
          initialEventKind={consentTarget.kind}
          onClose={() => setConsentTarget(null)}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="家族情報を削除"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        message={deleteTarget
          ? `「${fullName(deleteTarget)}」の家族情報を削除します。記録済みの要介護状態も表示されなくなります。よろしいですか?`
          : ''}
        onConfirm={() => { if (deleteTarget) deleteMut.mutate(deleteTarget); }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
